'use client'

import { FC, Dispatch, SetStateAction, useEffect } from 'react'
import Link from 'next/link'
import { MenuItem } from '@/root/shared-types'
import { useIntl } from 'react-intl'
import { isNonClickable, takeAnother } from '@/root/modules/tuuleliinid/AppLayout'
import { AvailableTheme, getMessages } from '@/lang'
import { useAuth, useConfig, useCustomRouter, useGetAuthToken, useGetTranslations } from '@/root/shared-hooks'
import { useGetProfileData } from '@/root/shared-hooks/useGetProfileData/useGetProfileData'
import { Button } from '@/root/ui/tuuleliinid'
import { APP_ROUTES, testIds } from '@/root/shared-consts'
import { CloseIcon } from '@/root/ui/shared/icons/CloseIcon'

interface MobileMenuProps {
  menuItemsLine1: MenuItem[]
  menuItemsLine2: MenuItem[]
  mobileMenuOpen: boolean
  setMobileMenuOpen: Dispatch<SetStateAction<boolean>>
}

const MobileMenu: FC<MobileMenuProps> = ({ menuItemsLine1, menuItemsLine2, mobileMenuOpen, setMobileMenuOpen }) => {
  const { formatMessage } = useIntl()
  const { theme } = useConfig()
  const { locale, locales, getPathWithLocale } = useCustomRouter()
  const otherLocale = takeAnother(locales, locale)
  const currentMessages = getMessages(theme as AvailableTheme)
  const { getTranslation } = useGetTranslations()
  const { isAuth } = useGetAuthToken()
  const { logOut } = useAuth()
  const { user } = useGetProfileData(isAuth)

  // Construct fullName from firstName and lastName
  const fullName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}`.trim() : undefined

  useEffect(() => {
    const shouldOpen = sessionStorage.getItem('mobileMenuOpen')

    if (shouldOpen === 'true') {
      setMobileMenuOpen(true)
      sessionStorage.removeItem('mobileMenuOpen')
    }
  }, [setMobileMenuOpen])

  if (!mobileMenuOpen) return null

  const items = menuItemsLine1.slice(0, 3)

  const clickable = items.filter((item) => !isNonClickable(item.subject))
  const nonClickable = items.filter((item) => isNonClickable(item.subject))

  const orderedItems = [...clickable, ...nonClickable]

  return (
    <div data-testid={testIds.header.mobileMenu} className="fixed inset-0 z-50 flex flex-col bg-white md:hidden">
      {/* Close */}
      <div className="flex justify-end p-5">
        <button onClick={() => setMobileMenuOpen(false)}>
          <CloseIcon color="primary-base" />
        </button>
      </div>

      {/* Auth actions */}
      <div className="mb-6 px-6">
        {isAuth ? (
          <div className="flex flex-col gap-3">
            <Link href={`/${locale}${APP_ROUTES.details}`} className="text-primary-base text-center font-semibold">
              {fullName ? (
                <>
                  {getTranslation('headerLoginButtonLabel')} <span className="uppercase">{fullName}</span>!
                </>
              ) : (
                getTranslation('profilePageTitle')
              )}
            </Link>
            <Button variant="contained" className="w-full py-3 font-semibold" onClick={logOut}>
              {getTranslation('headerLogoutButtonLabel')}
            </Button>
          </div>
        ) : (
          <Link href={`/${locale}${APP_ROUTES.signup}`}>
            <Button variant="contained" className="w-full py-3 font-semibold">
              {getTranslation('headerRegisterOrLoginButton')}
            </Button>
          </Link>
        )}
      </div>

      <nav className="flex flex-col gap-3 px-6 text-lg">
        <ul className="border-primary-base border-b">
          <li className="text-primary-base menu-link flex h-12 items-center font-bold">
            <Link
              href={getPathWithLocale(otherLocale)}
              onClick={() => sessionStorage.setItem('mobileMenuOpen', 'true')}
            >
              {formatMessage(currentMessages.headerLangSwitcher)}
            </Link>
          </li>
        </ul>

        <ul className="text-primary-base flex flex-col gap-3 font-bold">
          {menuItemsLine2
            ?.filter((item) => item.className !== 'login')
            .map(({ id, link, subject }) => (
              <li key={id}>
                <Link href={`/${locale}${link}`} onClick={() => setMobileMenuOpen(false)}>
                  {subject}
                </Link>
              </li>
            ))}
        </ul>

        <ul className="text-primary-base flex flex-col gap-3">
          {orderedItems.map(({ id, link, subject }) => {
            if (isNonClickable(subject)) {
              return (
                <li key={id} className="menu-link">
                  {subject}
                </li>
              )
            }

            return (
              <li key={id}>
                <Link href={`/${locale}${link}`} className="font-bold" onClick={() => setMobileMenuOpen(false)}>
                  {subject}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>
    </div>
  )
}

export default MobileMenu
